import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import SidePanel from "./SidePanel";
import ContentPanel from "./ContentPanel";
import Header from "./Header";
import { useUser } from "./context/UserContext";
import { ThemeProvider } from "./context/ThemeContext";
import { NotificationProvider } from "./context/NotificationContext";
import useFeatureAccess from "../../hooks/useFeatureAccess";
import NavigationGuard from "./components/NavigationGuard";
import UserAuthAPI from "./services/UserAuthAPI";
import UserAPI from "./services/UserAPI";
import ChatWidget from "./ChatWidget";
import ChatWidgetErrorBoundary from "./ChatWidgetErrorBoundary";
import ADAWidget from "./ADAWidget";
import { clearFrontendAuthData, hasFrontendAuthData } from "./utils/AuthUtil";
import { setCookieMinutes } from "./utils/CookieUtil";
import { SESSION_TTL_MINUTES } from "../../config/sessionConfig";
import {
  slugToHeroContent,
  heroContentToSlug,
} from "../../utils/sectionRoutes";
import "./css/Home.css";
import "./css/Theme.css";

// Inner component that has access to UserContext
function ApplicationInner({ handleHomeClick, showLogin }) {
  const { loggedInUser, isSessionTimedOut, isReadOnlyView } = useUser();
  const { canAccessFunctionalFeatures } = useFeatureAccess();
  const { section } = useParams(); // Read :section from /app/:section URL
  const location = useLocation();
  const navigate = useNavigate();
  const modalParam = new URLSearchParams(location.search).get("modal");
  const shouldOpenCreateAccount = modalParam === "create-account";
  const [heroContent, setHeroContent] = useState("");
  const [previousContent, setPreviousContent] = useState(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] =
    useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] =
    useState(false);
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);
  const [isPhoneVerificationPopupOpen, setIsPhoneVerificationPopupOpen] =
    useState(false);
  const [smsSuccessfullySent, setSmsSuccessfullySent] = useState(false);

  // Explicit in-page link navigation — records the current page as "previous"
  // so the back button can display "← Back to [page]".
  // Sidebar navigation calls setHeroContent directly and does NOT set previousContent.
  const heroContentRef = React.useRef(heroContent);
  React.useEffect(() => {
    heroContentRef.current = heroContent;
  }, [heroContent]);
  const navigateWithBack = React.useCallback((content) => {
    setPreviousContent(heroContentRef.current || null);
    setHeroContent(content);
  }, []);

  const goBack = React.useCallback(() => {
    if (previousContent) {
      setHeroContent(previousContent);
      setPreviousContent(null);
    }
  }, [previousContent]);

  // Check authentication status when component mounts and show login modal if not authenticated
  useEffect(() => {
    console.log("Application: Checking authentication status...", loggedInUser);
    console.log("Application: Current path:", location.pathname);

    if (!loggedInUser) {
      if (shouldOpenCreateAccount) {
        console.log(
          "Application: No logged in user found, showing create account modal",
        );
        setIsCreateAccountModalOpen(true);
        setIsLoginModalOpen(false);
      } else {
        // If no user is logged in, automatically show the login modal
        console.log(
          "Application: No logged in user found, showing login modal",
        );
        setIsLoginModalOpen(true);
        setIsCreateAccountModalOpen(false);
      }
      // Clear content when showing login modal
      setHeroContent("");
    } else {
      console.log("Application: User is already logged in:", loggedInUser);
      // If user is logged in, ensure auth modals are closed
      setIsLoginModalOpen(false);
      setIsCreateAccountModalOpen(false);
    }
  }, [
    loggedInUser,
    shouldOpenCreateAccount,
    showLogin,
    location.pathname,
    location.search,
    navigate,
  ]);

  // Set heroContent from URL :section parameter (deep linking)
  // Also clear previousContent so the back button is never shown on directly-accessed pages.
  useEffect(() => {
    if (section && loggedInUser) {
      const contentKey = slugToHeroContent(section);
      console.log(
        `Application: Deep link to section "${section}" → heroContent "${contentKey}"`,
      );
      setPreviousContent(null);
      setHeroContent(contentKey);
    }
  }, [section, loggedInUser]);

  // Validate and restore backend session on mount (moved from App.js handleManageAccountClick)
  useEffect(() => {
    const validateAndRestoreSession = async () => {
      // If user is already in context, nothing to restore
      if (loggedInUser) return;

      console.log("🔍 Application: Validating backend session...");
      try {
        const sessionStatus = await UserAuthAPI.checkSessionStatus();

        if (sessionStatus.authenticated) {
          console.log(
            "✅ Application: Backend session valid - userUuid:",
            sessionStatus.userUuid,
          );

          // Check if frontend cookies are missing and restore them
          if (!hasFrontendAuthData()) {
            console.log(
              "🔧 Application: Frontend cookies missing, restoring from backend session...",
            );
            try {
              const userData = await UserAPI.getUserByUuid(
                sessionStatus.userUuid,
              );
              if (userData && userData.uuid) {
                const cookieTTL = SESSION_TTL_MINUTES;
                setCookieMinutes("uuid", userData.uuid, cookieTTL, "/");
                setCookieMinutes("user", userData.uuid, cookieTTL, "/");
                console.log(
                  "✅ Application: Frontend cookies restored successfully",
                );
              }
            } catch (error) {
              console.error("❌ Application: Error fetching user data:", error);
            }
          }
        } else {
          console.log("❌ Application: Backend session invalid");
          if (hasFrontendAuthData()) {
            console.log("🧹 Application: Clearing stale frontend data");
            clearFrontendAuthData();
          }
        }
      } catch (error) {
        console.error("❌ Application: Error validating session:", error);
        if (hasFrontendAuthData()) {
          clearFrontendAuthData();
        }
      }
    };

    validateAndRestoreSession();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Function to check if screen size is responsive/mobile
  const isResponsiveSize = () => window.innerWidth <= 768;

  // Initialize SidePanel state based on screen size
  // Track numeric side panel width for content margin (100px when minimized, 320px when expanded)
  const [sidePanelWidth, setSidePanelWidth] = useState(
    isResponsiveSize() ? 100 : 320,
  );
  const [userInfo, setUserInfo] = useState(null);

  // Handle screen resize to auto-collapse SidePanel on responsive sizes
  useEffect(() => {
    const handleResize = () => {
      const shouldMinimize = isResponsiveSize();
      setSidePanelWidth(shouldMinimize ? 100 : 320);
    };

    window.addEventListener("resize", handleResize);

    // Set initial state on component mount
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleSidePanelToggle = (width) => {
    setSidePanelWidth(width);
  };

  const handleMenuSelect = (menuItem) => {
    // Sidebar navigation — clear back history so the back button is hidden
    setPreviousContent(null);
    setHeroContent(menuItem);
  };

  // Handle custom back navigation within the SPA
  const handleSPABackNavigation = (event) => {
    console.log("Application: Custom back navigation triggered");

    // Check if we have any modal open - close it instead of leaving
    if (isLoginModalOpen) {
      setIsLoginModalOpen(false);
      return;
    }
    if (isCreateAccountModalOpen) {
      setIsCreateAccountModalOpen(false);
      return;
    }
    if (isForgotPasswordModalOpen) {
      setIsForgotPasswordModalOpen(false);
      return;
    }
    if (isChangePasswordModalOpen) {
      setIsChangePasswordModalOpen(false);
      return;
    }
    if (isPhoneVerificationPopupOpen) {
      setIsPhoneVerificationPopupOpen(false);
      return;
    }

    // If no modals are open, check if we can navigate to a previous section
    if (heroContent && heroContent !== "Dashboard") {
      // Go back to dashboard
      setHeroContent("Dashboard");
      return;
    }

    // If already at dashboard, ask user if they want to go to main site
    const shouldGoHome = window.confirm(
      "Go back to the main KinShield website?",
    );

    if (shouldGoHome && handleHomeClick) {
      handleHomeClick();
    }
  };

  // Clear all application state when session times out and show login modal
  useEffect(() => {
    if (isSessionTimedOut) {
      console.log(
        "Application: Session timed out, clearing all application state and showing login modal",
      );
      // Clear all modal states except login modal
      setIsCreateAccountModalOpen(false);
      setIsForgotPasswordModalOpen(false);
      setIsChangePasswordModalOpen(false);
      setIsPhoneVerificationPopupOpen(false);

      // Clear content and navigation state
      setHeroContent("");

      // Reset side panel to default state
      setSidePanelWidth(320);

      // Show login modal for session timeout
      setIsLoginModalOpen(true);
    }
  }, [isSessionTimedOut]);

  return (
    <NavigationGuard
      onNavigateBack={handleSPABackNavigation}
      preventBrowserBack={true}
      showExitConfirmation={false} // We handle confirmation in custom handler
    >
      <div
        className={`home-container ${
          isLoginModalOpen ||
          isCreateAccountModalOpen ||
          isForgotPasswordModalOpen ||
          isChangePasswordModalOpen ||
          isPhoneVerificationPopupOpen
            ? "dimmed"
            : ""
        }`}
      >
        <Header
          toggleModal={setIsLoginModalOpen}
          toggleCreateAccountModal={setIsCreateAccountModalOpen}
          handleHomeClick={handleHomeClick}
          setHeroContent={handleMenuSelect}
          toggleChangePasswordModal={setIsChangePasswordModalOpen}
          isSessionTimedOut={isSessionTimedOut}
          heroContent={heroContent}
        />
        <div className="body-container">
          {!isReadOnlyView && (
            <SidePanel
              onToggleMinimize={handleSidePanelToggle}
              onMenuSelect={handleMenuSelect}
              setHeroContent={setHeroContent}
              currentContent={heroContent}
              isSessionTimedOut={isSessionTimedOut}
            />
          )}
          <ContentPanel
            handleHomeClick={handleHomeClick} // Pass handleHomeClick to ContentPanel
            sidePanelWidth={isReadOnlyView ? 0 : sidePanelWidth}
            isSidePanelMinimized={isReadOnlyView || sidePanelWidth === 100}
            setIsLoginModalOpen={setIsLoginModalOpen}
            setIsCreateAccountModalOpen={setIsCreateAccountModalOpen}
            setIsForgotPasswordModalOpen={setIsForgotPasswordModalOpen}
            setIsChangePasswordModalOpen={setIsChangePasswordModalOpen}
            setIsPhoneVerificationPopupOpen={setIsPhoneVerificationPopupOpen}
            isLoginModalOpen={isLoginModalOpen}
            isCreateAccountModalOpen={isCreateAccountModalOpen}
            isForgotPasswordModalOpen={isForgotPasswordModalOpen}
            isChangePasswordModalOpen={isChangePasswordModalOpen}
            isPhoneVerificationPopupOpen={isPhoneVerificationPopupOpen}
            setHeroContent={setHeroContent}
            heroContent={heroContent}
            previousContent={previousContent}
            goBack={goBack}
            navigateWithBack={navigateWithBack}
            isSessionTimedOut={isSessionTimedOut}
            smsSuccessfullySent={smsSuccessfullySent}
            setSmsSuccessfullySent={setSmsSuccessfullySent}
          />
        </div>
        {/* Floating Chat Assistant — Scout funnels into paid features (scam
            detection, training, etc.), so it is shown ONLY when the user can
            actually access functional features. This hides it in view-only
            mode and for ANY account without an active subscription (lapsed OR
            never-subscribed), preventing Scout from leading users into paid
            features they cannot use. Demo seats, trials, and grace-period users
            retain functional access and therefore still see Scout. */}
        {loggedInUser && !isReadOnlyView && canAccessFunctionalFeatures && (
          <ChatWidgetErrorBoundary>
            <ChatWidget
              setHeroContent={setHeroContent}
              currentPage={heroContent}
            />
          </ChatWidgetErrorBoundary>
        )}
        {/* Floating ADA Accessibility Widget */}
        <ADAWidget />
      </div>
    </NavigationGuard>
  );
}

// Main Application component that now receives UserContext from App.js
function Application({ handleHomeClick, showLogin }) {
  return (
    <ThemeProvider>
      <NotificationProvider>
        <ApplicationInner
          handleHomeClick={handleHomeClick}
          showLogin={showLogin}
        />
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default Application;
